import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { skipToken } from '@reduxjs/toolkit/query';
import { IconButton } from '@mui/material';
import PreviewIcon from '@mui/icons-material/Preview';
import GeoMapWithTiles from '../../../features/geo-map/GeoMapWithTiles';
import MarkerVectorLayer from '../../../shared/ui/GeoMap/MarkerVectorLayer';
import style from './GeoMap.module.css';
import useVehicleCoordinates from './hooks/useVehicleCoordinates';
import { useAppSelector } from '../../../app/store';
import reducerPath from '../../../app/reducerPath';
import mapNames from '../../../entities/map/constants';
import MapContainer from './styles/MapContainer';
import { useGetGeoZonesQuery } from '../../../entities/geoZones/geoZone/redux/geoZone.api';
import { parsePolygonWHoles } from '../../../shared/geo-map/lib';
import PolygonVectorLayer from '../../../shared/ui/GeoMap/PolygonVectorLayer';
import { FIELD_COLOR, UNLOADING_COLOR, WAITING_COLOR } from './constants';
import { DEFAULT_EXTENT } from '../../../features/geo-map/GeoMap.constants';
import isArrayEqual from '../../../shared/utils/isArrayEqual';
import getExtentFromFeatures from '../../../shared/ui/GeoMap/utils/getExtentFromFeatures';

function LogisticMapWidget({ logisticTask }) {
  const logisticTaskId = logisticTask?.data?.logisticTaskId;
  const storedExtent = localStorage.getItem(`extent_${logisticTaskId}`);

  const [extent, setExtent] = useState(null);
  const [isExtentWas, setIsExtentWas] = useState(false);
  const [isAnimatingExtent, setIsAnimatingExtent] = useState(false);

  const mapName = mapNames.powerBalancing;
  const savedView = useAppSelector((state) => state[`${reducerPath.positionMap}`][mapName]);
  const isFullScreen = useAppSelector((state) => state[`${reducerPath.fullScreenMap}`])[mapName];

  const fieldGeozoneIds = logisticTask?.data?.source?.fieldDetailsList?.map((field) => field.geozoneId) || [];
  const unloadGeozoneIds = logisticTask?.data?.source?.warehouseDetailsList?.map((wh) => wh.geozoneUnloadId) || [];
  const waitGeozoneIds = logisticTask?.data?.source?.warehouseDetailsList?.map((wh) => wh.geozoneWaitId) || [];

  const allGeozoneIds = [...fieldGeozoneIds, ...unloadGeozoneIds, ...waitGeozoneIds];

  const { data } = useGetGeoZonesQuery(
    allGeozoneIds?.length
      ? { params: { 'geozoneId.in': allGeozoneIds.join(','), size: allGeozoneIds.length } }
      : skipToken,
  );

  const geoZones = data?.data?.map((geozone) => ({
    ...geozone,
    polygon: parsePolygonWHoles(geozone.area),
  }));

  const vehicles = useMemo(() => {
    return [
      ...(logisticTask?.data?.source?.truckDetailsList || []),
      ...(logisticTask?.data?.source?.harvesterDetailsList || []),
    ];
  }, [logisticTask?.data?.source?.truckDetailsList, logisticTask?.data?.source?.harvesterDetailsList]);

  const vehiclesCoords = useVehicleCoordinates(logisticTaskId, vehicles);

  // устанавливаем extent из localstorage, если он есть
  useEffect(() => {
    if (storedExtent) {
      try {
        const parsed = JSON.parse(storedExtent);
        setExtent(parsed);
        setIsExtentWas(true);
      } catch (err) {
        // eslint-disable-next-line
        console.warn('Ошибка парсинга extent из localStorage', err);
      }
    }
  }, [logisticTaskId]);

  useEffect(() => {
    const currentExtent = savedView?.extent || extent;
    if (logisticTaskId && currentExtent && !isArrayEqual(currentExtent, DEFAULT_EXTENT)) {
      localStorage.setItem(`extent_${logisticTaskId}`, JSON.stringify(currentExtent));
    }
  }, [logisticTaskId, savedView, extent]);

  const fitAllFeatures = () => {
    if (!geoZones?.length && !vehiclesCoords?.length) return;

    const polygons = geoZones?.map((g) => g.polygon) || [];
    const points = vehiclesCoords?.filter((v) => Array.isArray(v.coordinates))?.map((v) => v.coordinates) || [];

    const newExtent = getExtentFromFeatures(polygons, points);
    if (newExtent) setExtent(newExtent);
  };

  useEffect(() => {
    if (storedExtent || isExtentWas) return;

    // если пользователь уже двигал карту - показываем сохраненное положение
    // иначе устанавливаем начальное положение
    if (savedView?.extent && !isArrayEqual(savedView.extent, DEFAULT_EXTENT)) {
      setExtent(savedView.extent);
      setIsExtentWas(true);
    } else if (geoZones?.length || vehiclesCoords?.length) {
      fitAllFeatures();
      setIsExtentWas(true);
    }
  }, [logisticTaskId, savedView, geoZones, vehiclesCoords]);

  const handleFitFeatures = () => {
    setIsAnimatingExtent(true);
    fitAllFeatures();
  };

  return (
    <MapContainer isFullScreen={isFullScreen}>
      <GeoMapWithTiles
        name={mapName}
        isAnimateOnExtent={isAnimatingExtent}
        extent={extent}
        styles={isFullScreen ? style.fullGeoMap : style.geoMap}
        isOffButtonFollowMode
        customButtons={[
          <IconButton title="Показать все" onClick={handleFitFeatures}>
            <PreviewIcon />
          </IconButton>,
        ]}
      >
        {geoZones?.map((geoZone) => {
          let color;
          const isField = fieldGeozoneIds.includes(geoZone.geozoneId);
          if (isField) color = FIELD_COLOR;
          if (unloadGeozoneIds.includes(geoZone.geozoneId)) color = UNLOADING_COLOR;
          if (waitGeozoneIds.includes(geoZone.geozoneId)) color = WAITING_COLOR;

          return (
            <PolygonVectorLayer
              key={geoZone.id}
              disableAutoZoom
              coordinates={geoZone.polygon}
              text={geoZone.name}
              fillColor={color}
              strokeColor={color?.slice(0, -2)}
              isCenterText={isField}
            />
          );
        })}

        {vehiclesCoords?.map((vehicle) => (
          <MarkerVectorLayer
            key={vehicle.vehicleId}
            vehicleModelId={vehicle.vehicleModelId}
            vehicleIconColor={vehicle.vehicleIconColor}
            coordinates={vehicle.coordinates}
            text={vehicle.num}
            icoColor={vehicle.icoColor}
          />
        ))}
      </GeoMapWithTiles>
    </MapContainer>
  );
}

LogisticMapWidget.propTypes = {
  logisticTask: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object, PropTypes.array]),
  ),
};

LogisticMapWidget.defaultProps = {
  logisticTask: null,
};

export default LogisticMapWidget;
